import { useCallback, useEffect, useReducer, useRef } from 'react'
import { EditingLeaseRead, EditingLeaseErrorCode, type EditingLeaseErrorDetail, getEditingLeaseError } from '../schemas'
import { canWriteWithLease, editingLeaseReducer, EditingLeasePhase, type EditingLeaseEvent } from '../utils/editing-lease-state'

export type EditingLeaseApi = {
  status: () => Promise<EditingLeaseRead>
  acquire: (age?: number) => Promise<EditingLeaseRead>
  renew: (age?: number) => Promise<EditingLeaseRead>
}

type Options = {
  resource: EditingLeaseRead['resource']
  eligible: boolean
  ready: boolean
  enabled: boolean
  api: EditingLeaseApi
  refresh: (isCurrent: () => boolean) => Promise<void>
  onStartEditingFailure?: () => void
}

/** Mounted once per authenticated resource; waiting views may rejoin the user's live lease. */
export function useEditingLease({ resource, eligible, ready, enabled, api, refresh, onStartEditingFailure }: Options) {
  const [state, dispatch] = useReducer(editingLeaseReducer, {
    lease: null,
    enabled,
    phase: EditingLeasePhase.INITIAL,
    pending: false,
    deadline: null,
  })
  // Async callbacks read the latest reducer snapshot even before React renders queued events.
  const snapshot = useRef(state)
  const send = useCallback((event: EditingLeaseEvent) => {
    snapshot.current = editingLeaseReducer(snapshot.current, event)
    dispatch(event)
  }, [])
  const entered = useRef(false)
  const lastInput = useRef<number | undefined>(undefined)
  const start = useRef<() => void>(() => {})
  const recover = useRef<(detail: EditingLeaseErrorDetail) => boolean>(() => false)
  const activity = useRef<() => void>(() => {})
  const handleDenial = useCallback((detail: EditingLeaseErrorDetail) => recover.current(detail), [])
  const handleError = useCallback(
    (failure: unknown) => {
      const detail = getEditingLeaseError(failure)
      return detail ? handleDenial(detail) : false
    },
    [handleDenial]
  )
  const expiryTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // Keep a known deadline across flag changes, even when the next status read is unavailable.
  useEffect(() => () => clearTimeout(expiryTimer.current), [])

  useEffect(() => {
    send({ type: 'configure', enabled })
    if (!ready || !eligible) return
    // Cleanup invalidates this effect; an interrupted first acquisition must retry on remount.
    let cancelled = false
    let initialAcquirePending = false
    // Serialize requests, retaining explicit entry and urgent input received during a request.
    let busy = false
    let acquireQueued: { onFailure?: () => void } | null = null
    let activityPending = false
    // Ignore responses superseded by an explicit entry or a lease denial.
    let epoch = 0
    let timer: ReturnType<typeof setTimeout>

    const matches = (lease: EditingLeaseRead) =>
      lease.resource.kind === resource.kind && lease.resource.resource_id === resource.resource_id && lease.resource.matter_id === resource.matter_id
    const accept = (event: EditingLeaseEvent) => {
      send(event)
      clearTimeout(expiryTimer.current)
      const { deadline } = snapshot.current
      if (deadline !== null) expiryTimer.current = setTimeout(() => send({ type: 'expired' }), Math.max(0, deadline - performance.now()))
    }
    const correctMode = (detail: EditingLeaseErrorDetail) => {
      if (!matches(detail.lease)) return false
      accept({ type: 'conflict', lease: detail.lease, enabled: detail.code !== EditingLeaseErrorCode.DISABLED, now: performance.now() })
      return true
    }
    const nearDeadline = () => {
      const { deadline, lease } = snapshot.current
      return deadline !== null && deadline - performance.now() <= (lease?.heartbeat_interval_seconds ?? 30) * 1000
    }
    const run = async (acquire = false, onFailure?: () => void) => {
      if (cancelled) return
      if (busy) {
        if (acquire && !acquireQueued) {
          acquireQueued = { onFailure }
          // Explicit entry supersedes the background response without overlapping requests.
          epoch++
          send({ type: 'acquiring' })
        }
        return
      }
      busy = true
      const requestEpoch = ++epoch
      clearTimeout(timer)
      if (acquire) send({ type: 'acquiring' })
      try {
        const age = document.visibilityState === 'visible' && lastInput.current !== undefined ? (performance.now() - lastInput.current) / 1000 : undefined
        try {
          const current = snapshot.current
          const lease = current.enabled
            ? await (acquire ? api.acquire(age) : current.phase === EditingLeasePhase.EDITING ? api.renew(age) : api.status())
            : await api.status()
          if (cancelled || requestEpoch !== epoch) return
          if (!matches(lease)) throw new Error('Editing lease resource mismatch')
          accept({ type: 'received', lease, now: performance.now(), beforeEntry: !entered.current })
        } catch (failure) {
          if (cancelled || requestEpoch !== epoch) return
          const detail = getEditingLeaseError(failure)
          if (!detail || !correctMode(detail)) throw failure
        } finally {
          if (acquire) initialAcquirePending = false
        }
        if (snapshot.current.enabled && snapshot.current.phase === EditingLeasePhase.REFRESHING) {
          await refresh(() => !cancelled && requestEpoch === epoch)
          if (cancelled || requestEpoch !== epoch) return
          // Refresh may take long enough to outlive the lease; revalidate before enabling.
          const confirmed = await api.status()
          if (cancelled || requestEpoch !== epoch) return
          if (!matches(confirmed)) throw new Error('Editing lease resource mismatch')
          accept({ type: 'received', lease: confirmed, now: performance.now(), confirmed: true })
        }
      } catch {
        if (!cancelled && requestEpoch === epoch) {
          send({ type: 'failed' })
          onFailure?.()
        }
      } finally {
        busy = false
        if (!cancelled) {
          if (acquireQueued && snapshot.current.enabled) {
            const queued = acquireQueued
            acquireQueued = null
            activityPending = false
            void run(true, queued.onFailure)
          } else {
            acquireQueued = null
            send({ type: 'settled' })
            // Input arriving during an in-flight heartbeat must also reach the server before expiry.
            if (activityPending && snapshot.current.enabled && canWriteWithLease(snapshot.current) && nearDeadline()) {
              activityPending = false
              void run()
            } else {
              activityPending = false
              if (snapshot.current.enabled) {
                timer = setTimeout(() => void run(), requestEpoch !== epoch ? 0 : (snapshot.current.lease?.heartbeat_interval_seconds ?? 30) * 1000)
              }
            }
          }
        }
      }
    }
    activity.current = () => {
      if (!canWriteWithLease(snapshot.current) || document.visibilityState !== 'visible') return
      lastInput.current = performance.now()
      if (snapshot.current.enabled && nearDeadline()) {
        if (busy) activityPending = true
        else void run()
      }
    }
    start.current = () => {
      if (snapshot.current.enabled && !snapshot.current.pending) void run(true, onStartEditingFailure)
    }
    recover.current = (detail) => {
      if (!correctMode(detail)) return false
      epoch++
      // Reconcile on the next tick; never replay the rejected mutation.
      clearTimeout(timer)
      if (snapshot.current.enabled) timer = setTimeout(() => void run(), 0)
      return true
    }
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      if (!entered.current) {
        entered.current = true
        initialAcquirePending = snapshot.current.enabled && snapshot.current.phase === EditingLeasePhase.INITIAL
        if (snapshot.current.enabled) void run(initialAcquirePending)
      } else if (snapshot.current.enabled) void run()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    window.addEventListener('online', onVisible)
    window.addEventListener('pageshow', onVisible)
    // Disabled workspaces read once; known holders then drain through the local expiry timer.
    if (!snapshot.current.enabled) {
      onVisible()
      void run()
    } else if (!entered.current && document.visibilityState === 'visible') onVisible()
    else void run()
    return () => {
      if (initialAcquirePending) entered.current = false
      cancelled = true
      acquireQueued = null
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
      window.removeEventListener('online', onVisible)
      window.removeEventListener('pageshow', onVisible)
      start.current = () => {}
      recover.current = () => false
      activity.current = () => {}
    }
  }, [resource.kind, resource.resource_id, resource.matter_id, eligible, ready, enabled, api, refresh, onStartEditingFailure, send])

  return {
    lease: state.lease,
    canWrite: eligible && (!state.enabled || ready) && canWriteWithLease(state),
    initializing: state.enabled && state.phase === EditingLeasePhase.INITIAL,
    pending: state.pending,
    enabled: state.enabled,
    startEditing: () => start.current(),
    handleError,
    handleDenial,
    recordActivity: () => activity.current(),
  }
}
